import React, { useRef, useEffect, useState } from 'react';
import { getServicePrice, stripHtml } from './utils';
import AnatomyModal from '../components/booking/AnatomyModal';
import CartBanner from '../components/booking/CartBanner';

// Words that flag medical / safety content — those descriptions must never
// be truncated behind a Learn More tap.
const SAFETY_KEYWORDS = /\b(not suitable|warning|contraindication|medication|pregnant|avoid|accutane)\b/i;

function getDescriptionDisplay(plainDesc) {
  if (!plainDesc) return { text: '', hasMore: false };
  if (SAFETY_KEYWORDS.test(plainDesc)) {
    return { text: plainDesc, hasMore: false };
  }
  // Short — show in full.
  if (plainDesc.length <= 180) {
    return { text: plainDesc, hasMore: false };
  }
  // Longer — truncate at the first natural sentence boundary at least 60 chars deep.
  const match = plainDesc.match(/^([\s\S]{60,}?[.!?])\s/);
  if (match && plainDesc.length - match[1].length > 20) {
    return { text: match[1], hasMore: true };
  }
  // No clean break — fall back to showing the whole description.
  return { text: plainDesc, hasMore: false };
}

function ServiceSelectionStep({
  guestCount, currentGuestIndex, selectedCenter,
  loading, categories, selectedCategory, onCategorySelect,
  loadingCategoryServices, filteredServices, subCategories, displayServices,
  selectedSubCategory, onSubCategorySelect, onShowDetail,
  serviceSearch, onSearchChange, serviceGroups, searchResults, searchLoading,
  selectedServices, onToggleService, proceedingFromServices,
  needsAnatomy = false, anatomy = 'V', onAnatomyChange,
  allCenterServices, onBackToCategories
}) {
  // If user lands on an anatomy-required category without a choice (deep link / refresh),
  // open the modal to force an explicit selection.
  const [showAnatomyModal, setShowAnatomyModal] = useState(false);
  useEffect(() => {
    if (needsAnatomy && !anatomy) setShowAnatomyModal(true);
  }, [needsAnatomy, anatomy]);

  function handleAnatomyChoice(value) {
    onAnatomyChange?.(value);
    setShowAnatomyModal(false);
  }

  // Collapsible search — icon button by default, full-width input when expanded.
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  // Reset search on mount (covers nav-away-and-back)
  useEffect(() => {
    if (serviceSearch) onSearchChange?.('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Auto-focus when expanded
  useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  function handleSearchBlur() {
    // Collapse only if the input is empty — keep visible while user has text.
    if (!serviceSearch) setSearchExpanded(false);
  }

  function handleSearchClear() {
    onSearchChange?.('');
    setSearchExpanded(false);
  }

  // If the active sub-category doesn't exist in the current filtered list (e.g. user
  // flipped V → P and the prior sub no longer applies), fall back to "All".
  useEffect(() => {
    if (selectedSubCategory && selectedSubCategory !== 'all' && !subCategories.includes(selectedSubCategory)) {
      onSubCategorySelect?.('all');
    }
  }, [selectedSubCategory, subCategories, onSubCategorySelect]);
  const servicesRef = useRef(null);

  // On mobile, scroll to the services list when a category is selected
  useEffect(() => {
    if (selectedCategory && servicesRef.current && window.innerWidth <= 768) {
      servicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedCategory]);

  const Spinner = ({ text }) => (
    <div className="loading">
      <div className="spinner"></div>
      <span>{text || 'Loading...'}</span>
    </div>
  );

  const isSelected = (service) => selectedServices.some(s => s.id === service.id);

  const renderService = (service) => {
    const price = getServicePrice(service);
    const serviceName = service.display_name || service.name;
    const rawDesc = service.html_description || service.description || service.catalog_info?.description || '';
    const desc = rawDesc.toLowerCase().includes('click to view') ? '' : rawDesc;
    const plainDesc = stripHtml(desc);
    const { text: shortDesc, hasMore } = getDescriptionDisplay(plainDesc);
    const selected = isSelected(service);
    return (
      <div key={service.id} className={`service-card ${selected ? 'selected' : ''}`}
        onClick={() => onToggleService(service)}>
        <div className={`service-card-checkbox ${selected ? 'checked' : ''}`}>
          {selected && '✓'}
        </div>
        <div className="service-info">
          <h4>{serviceName}</h4>
          {shortDesc && <p className="service-description">{shortDesc}</p>}
          {hasMore && (
            <button className="learn-more-link" onClick={(e) => { e.stopPropagation(); onShowDetail({ ...service, _fullDesc: desc, _name: serviceName, _price: price }); }}>
              Learn More &rsaquo;
            </button>
          )}
        </div>
        <div className="service-bottom">
          <span className="service-duration">{service.duration} Mins</span>
          <span className="service-price">{price > 0 ? `$${price}` : 'Price varies'}</span>
        </div>
      </div>
    );
  };

  // Group services with subcategory headers for rendering
  const renderGrouped = (services) => {
    const grouped = [];
    let lastSub = null;
    for (const svc of services) {
      const sub = svc.sub_category_name || svc.catalog_info?.sub_category_name || '';
      if (sub && sub !== lastSub) {
        grouped.push({ type: 'header', label: sub, key: `sub-${sub}` });
        lastSub = sub;
      }
      grouped.push({ type: 'service', svc, key: svc.id });
    }
    return grouped.map(item =>
      item.type === 'header'
        ? <div key={item.key} className="service-subcategory-header">{item.label}</div>
        : renderService(item.svc)
    );
  };

  const isSearching = serviceSearch && serviceSearch.length > 0;

  return (
    <div className="step-content">
      <div className="service-back-row">
        <button type="button" className="service-back-pill" onClick={onBackToCategories}>
          ← All categories
        </button>
        <button
          type="button"
          className="service-search-icon-btn"
          onClick={() => setSearchExpanded(true)}
          aria-label="Search services"
          aria-expanded={searchExpanded}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
      {needsAnatomy && anatomy && (
        <p className="anatomy-summary">
          Showing services for <strong>{anatomy === 'V' ? 'vulva' : 'penis'}</strong>
          <span className="anatomy-summary-sep" aria-hidden="true"> · </span>
          <button type="button" className="anatomy-summary-change" onClick={() => setShowAnatomyModal(true)}>
            change
          </button>
        </p>
      )}
      <h1 className="step-title">
        {guestCount === 1 ? 'Choose your services' : `Guest ${currentGuestIndex + 1}: Choose your services`}
      </h1>

      {/* Cart banner at top */}
      <CartBanner
        selectedServices={selectedServices}
        onToggleService={onToggleService}
        onAddAnother={onBackToCategories}
      />

      {/* Expanded search input — only when user taps the icon */}
      {searchExpanded && (
        <div className="service-search-bar">
          <input
            ref={searchInputRef}
            type="text"
            className="service-search-input"
            placeholder="Search services..."
            value={serviceSearch || ''}
            onChange={e => onSearchChange(e.target.value)}
            onBlur={handleSearchBlur}
          />
          <button className="service-search-clear" onClick={handleSearchClear} aria-label="Close search">✕</button>
        </div>
      )}

      {proceedingFromServices && <Spinner text="Loading add-ons..." />}

      {loading ? <Spinner text="Loading categories..." /> : isSearching ? (
        /* Search results view */
        <div className="subcategory-accordions">
          {searchLoading ? (
            <Spinner text="Searching..." />
          ) : searchResults.length === 0 ? (
            <div className="prompt-message">No services found for "{serviceSearch}"</div>
          ) : (
            <div className="services-list">
              {renderGrouped(searchResults)}
            </div>
          )}
        </div>
      ) : (
        <>
          <AnatomyModal open={showAnatomyModal} onChoose={handleAnatomyChoice} />
          {selectedCategory && (
            <div className="subcategory-accordions" ref={servicesRef}>
              {subCategories.length > 0 && (
                <div className="services-sticky-bar services-sticky-bar--compact">
                  <div className="subcategory-pill-row" role="tablist" aria-label="Filter by sub-category">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selectedSubCategory === 'all'}
                      className={`subcategory-pill ${selectedSubCategory === 'all' ? 'active' : ''}`}
                      onClick={() => onSubCategorySelect?.('all')}
                    >
                      All
                    </button>
                    {subCategories.map(sub => (
                      <button
                        key={sub}
                        type="button"
                        role="tab"
                        aria-selected={selectedSubCategory === sub}
                        className={`subcategory-pill ${selectedSubCategory === sub ? 'active' : ''}`}
                        onClick={() => onSubCategorySelect?.(sub)}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loadingCategoryServices ? (
                <Spinner text="Loading services..." />
              ) : filteredServices.length === 0 ? (
                <div className="prompt-message">No services found for {selectedCategory.name}</div>
              ) : (
                <div className="services-list">
                  {selectedSubCategory === 'all' || !subCategories.includes(selectedSubCategory)
                    ? renderGrouped(displayServices)
                    : displayServices.map(renderService)
                  }
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ServiceSelectionStep;
